import React, { useEffect, useState } from "react";
import axios from "axios";
import PostagemCard from "./components/PostagemCard";
import SocialLayout from "./SocialLayout";
import { PostagemDTO } from "./types";
import styles from "./styles/Perfil.module.css";
import NovaPostagem from "./components/NovaPostagem";
import { useAuth } from "../auth/AuthProvider";

interface Stats {
  totalPostagens: number;
  totalCurtidas: number;
}

const PerfilPage: React.FC = () => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<"minhas" | "curtidas">("minhas");
  const [postagens, setPostagens] = useState<PostagemDTO[]>([]);
  const [postagensCurtidas, setPostagensCurtidas] = useState<PostagemDTO[]>([]);
  const [stats, setStats] = useState<Stats>({ totalPostagens: 0, totalCurtidas: 0 });
  const [carregando, setCarregando] = useState(false);
  const [postagemEditando, setPostagemEditando] = useState<PostagemDTO | null>(null);

  // 👉 Função para abrir o modal de login/cadastro
  const openRegisterModal = () => {
    const modalEl = document.getElementById("register-modal");
    if (modalEl) {
      const bootstrapModal = new (window as any).bootstrap.Modal(modalEl);
      bootstrapModal.show();
    }
  };

  // 🔹 Se o usuário não estiver logado, abre o modal
  useEffect(() => {
    if (!user) {
      openRegisterModal();
    }
  }, [user]);

  // 🔹 Buscar postagens do usuário
  const fetchPostagens = async () => {
    if (!user) return; // evita requisição se não logado
    setCarregando(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get<PostagemDTO[]>(`${BASE_URL}/postagens/minhas`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setPostagens(res.data);
    } catch (error) {
      console.error("Erro ao carregar postagens:", error);
    } finally {
      setCarregando(false);
    }
  };

  // 🔹 Buscar postagens curtidas
  const fetchPostagensCurtidas = async () => {
    if (!user) return;
    setCarregando(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get<PostagemDTO[]>(`${BASE_URL}/postagens/curtidas`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setPostagensCurtidas(res.data);
    } catch (error) {
      console.error("Erro ao carregar postagens curtidas:", error);
    } finally {
      setCarregando(false);
    }
  };

  // 🔹 Buscar estatísticas
  const fetchStats = async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get<Stats>(`${BASE_URL}/postagens/minhas/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setStats(res.data);
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPostagens();
      fetchStats();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "curtidas" && user) {
      fetchPostagensCurtidas();
    }
  }, [activeTab, user]);

  const listaPostagens = activeTab === "minhas" ? postagens : postagensCurtidas;

  const handlePostagemAtualizada = () => {
    setPostagemEditando(null);
    fetchPostagens();
    fetchStats();
  };

  return (
    <SocialLayout title="Meu Perfil">
      {/* Se o usuário não estiver logado, mostra algo simples até ele logar */}
      {!user ? (
        <div className="text-center py-5">
          <p>Por favor, entre ou crie uma conta para acessar seu perfil.</p>
        </div>
      ) : (
        <>
          {/* Header do perfil */}
          <div className={styles.perfilHeader}>
            <div className={styles.perfilFotoCol}>
              <img
                src={
                  user?.foto
                    ? `${BASE_URL}${user.foto.url.replace(/^\/api/, "")}`
                    : "/assets/img/users/user-generic.jpg"
                }
                alt={user?.nome}
                className={styles.perfilFoto}
              />
              <h3 className={styles.perfilNome}>{user?.nome}</h3>
            </div>

            <div className={styles.perfilStatsCol}>
              <div className={styles.statCard}>
                <h4>{stats.totalPostagens}</h4>
                <p>Postagens</p>
              </div>
              <div className={styles.statCard}>
                <h4>{stats.totalCurtidas}</h4>
                <p>Curtidas</p>
              </div>
            </div>
          </div>

          {/* Criar ou editar postagem */}
          <NovaPostagem
            key={postagemEditando?.id || "nova"}
            postagemParaEditar={postagemEditando || undefined}
            onPostCreated={handlePostagemAtualizada}
          />

          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tabButton} ${
                activeTab === "minhas" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("minhas")}
            >
              Minhas Postagens
            </button>
            <button
              className={`${styles.tabButton} ${
                activeTab === "curtidas" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("curtidas")}
            >
              Postagens Curtidas
            </button>
          </div>

          {/* Conteúdo */}
          <div className={styles.postagensContainer}>
            {carregando && <p className={styles.loader}>Carregando...</p>}
            {!carregando && listaPostagens.length === 0 && (
              <p>Nenhuma postagem encontrada.</p>
            )}
            {!carregando &&
              listaPostagens.map((postagem) => (
                <PostagemCard
                  key={postagem.id}
                  postagem={postagem}
                  onExcluir={(id) => {
                    if (activeTab === "minhas") {
                      setPostagens((prev) => prev.filter((p) => p.id !== id));
                      fetchStats();
                    }
                  }}
                  onEditar={(post) => setPostagemEditando(post)}
                />
              ))}
          </div>
        </>
      )}
    </SocialLayout>
  );
};

export default PerfilPage;